import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download, RefreshCw } from 'lucide-react'
import {
  Button,
  FormField,
  FormRow,
  FormSection,
  ProductPageShell,
  StatePanel,
} from '@/components/common'
import { useOrganization } from '@/contexts/OrganizationContext'
import { igamingApi } from '@/services/api'
import { CollapsibleFieldGroup } from './shared/CollapsibleFieldGroup'
import { InsightsTable } from './shared/InsightsTable'
import { EMPTY_ANALYTICS_FILTERS, PAGE_COPY } from './shared/ruleConstants'
import styles from './shared/igaming.module.css'

function IGamingInsightsPage() {
  const { currentOrg, hasProductAccess } = useOrganization()
  const slug = currentOrg?.slug
  const [analyticsFilters, setAnalyticsFilters] = useState(EMPTY_ANALYTICS_FILTERS)

  const canAnalyze = hasProductAccess('igaming_search', 'analyze')
  const pageCopy = PAGE_COPY.insights

  const analyticsParams = useMemo(() => {
    return Object.fromEntries(
      Object.entries(analyticsFilters)
        .map(([key, value]) => [key, String(value || '').trim()])
        .filter(([, value]) => value)
    )
  }, [analyticsFilters])

  const analyticsQuery = useQuery({
    queryKey: ['igaming', slug, 'analytics', analyticsParams],
    queryFn: () => igamingApi.getAnalytics(slug, analyticsParams),
    enabled: Boolean(slug) && canAnalyze,
  })

  const analytics = analyticsQuery.data || {}
  const insights = analytics.items || []
  const zeroResultSearches = analytics.zero_result_searches || []
  const lowResultSearches = analytics.low_result_searches || []
  const rulePerformance = analytics.rule_performance || []

  return (
    <ProductPageShell
      title={pageCopy.title}
      subtitle={pageCopy.subtitle}
      actions={canAnalyze ? (
        <Button variant="secondary" onClick={() => {
          analyticsQuery.refetch()
        }}>
          <RefreshCw size={16} />
          Refresh
        </Button>
      ) : null}
    >
      <FormSection title="Search activity">
        {!canAnalyze ? (
          <StatePanel state="restricted" title="Analytics access required" />
        ) : (
          <div className={styles.stack}>
            <div className={styles.filterPanel}>
              <FormRow>
                <FormField label="From" type="date" value={analyticsFilters.from} onChange={(event) => setAnalyticsFilters((current) => ({ ...current, from: event.target.value }))} />
                <FormField label="To" type="date" value={analyticsFilters.to} onChange={(event) => setAnalyticsFilters((current) => ({ ...current, to: event.target.value }))} />
              </FormRow>
              <FormRow>
                <FormField label="Regulation" value={analyticsFilters.market} placeholder="NJ" onChange={(event) => setAnalyticsFilters((current) => ({ ...current, market: event.target.value }))} />
                <FormField label="Device" value={analyticsFilters.device} placeholder="desktop, mobile" onChange={(event) => setAnalyticsFilters((current) => ({ ...current, device: event.target.value }))} />
              </FormRow>
              <CollapsibleFieldGroup title="Advanced filters">
                <FormRow>
                  <FormField label="Rule type" value={analyticsFilters.rule_type} placeholder="promo_boost, jackpot_rule" onChange={(event) => setAnalyticsFilters((current) => ({ ...current, rule_type: event.target.value }))} />
                  <FormField label="Provider" value={analyticsFilters.provider} placeholder="Pragmatic Play" onChange={(event) => setAnalyticsFilters((current) => ({ ...current, provider: event.target.value }))} />
                </FormRow>
                <FormField label="Theme" value={analyticsFilters.theme} placeholder="irish, jackpot, ancient egypt" onChange={(event) => setAnalyticsFilters((current) => ({ ...current, theme: event.target.value }))} />
              </CollapsibleFieldGroup>
            </div>
            <div className={styles.formActions}>
              <Button
                variant="secondary"
                onClick={() => setAnalyticsFilters(EMPTY_ANALYTICS_FILTERS)}
              >
                Reset filters
              </Button>
              <Button
                variant="secondary"
                onClick={async () => {
                  const blob = await igamingApi.downloadAnalytics(slug, analyticsParams)
                  const url = URL.createObjectURL(blob)
                  const anchor = document.createElement('a')
                  anchor.href = url
                  anchor.download = 'igaming-analytics.csv'
                  anchor.click()
                  URL.revokeObjectURL(url)
                }}
              >
                <Download size={16} />
                Export CSV
              </Button>
            </div>
            {analyticsQuery.isLoading ? <StatePanel state="loading" title="Loading iGaming insights" /> : null}
            {analyticsQuery.isError ? (
              <StatePanel
                state="error"
                title="iGaming insights failed"
                description={analyticsQuery.error?.response?.data?.error || analyticsQuery.error?.message}
                onRetry={() => analyticsQuery.refetch()}
              />
            ) : null}
            {!analyticsQuery.isLoading && !analyticsQuery.isError ? (
              <>
                <InsightsTable title="Top searches" firstColumn="Query" items={insights} />
                <InsightsTable title="Zero-result searches" firstColumn="Query" items={zeroResultSearches} />
                <InsightsTable title="Low-result searches" firstColumn="Query" items={lowResultSearches} />
                <InsightsTable title="Rule performance" firstColumn="Rule ID" items={rulePerformance} />
                {!insights.length && !zeroResultSearches.length && !lowResultSearches.length && !rulePerformance.length ? (
                  <StatePanel state="empty" title="No iGaming analytics yet" />
                ) : null}
              </>
            ) : null}
          </div>
        )}
      </FormSection>
    </ProductPageShell>
  )
}

export default IGamingInsightsPage
